import React, { useState } from "react"
import { Form } from "react-bootstrap"
import { usePayLogic } from "../usePayLogic"
import AddonDropdown from "@features/Price/Addons/AddonDropdown"
import * as styles from "./style.module.scss"

interface AddonPriceControlProps {
  price: number
  bucket: string
  title: string
  addonKey: string
}

const AddonPriceControl: React.FC<AddonPriceControlProps> = ({ price, bucket, title, addonKey }) => {
  const {
    id,
    text,
    checked,
    isAIBot,
    isMessengers,
    isDropdown,
    selectedPrice,
    selectedCount,
    localIndex,
    aiBotOptions,
    aiBotPricing,
    emailOptions,
    emailPricing,
    messengersOptions,
    messengersPricing,
    handleToggle,
    handleSelect,
  } = usePayLogic({ price, bucket, title, addonKey })

  const [open, setOpen] = useState(false)

  if (!isDropdown) {
    return (
      <div className={styles.switchWrapper}>
        <Form.Check type="switch" name={bucket}>
          <Form.Check.Input id={id} checked={checked} onChange={handleToggle} />
          <Form.Check.Label htmlFor={id}>
            {selectedPrice.toLocaleString("ru-RU")}
            <span dangerouslySetInnerHTML={{ __html: text.addonText.rub }} />
            <span dangerouslySetInnerHTML={{ __html: text.addonText.after }} />
          </Form.Check.Label>
        </Form.Check>
      </div>
    )
  }

  const mode = isAIBot ? "ai" : isMessengers ? "messengers" : "email"
  const config = addonDropdownConfig[mode]

  return (
    <AddonDropdown
      id={id}
      bucket={bucket}
      checked={checked}
      open={open}
      setOpen={setOpen}
      selectedPrice={selectedPrice}
      selectedCount={selectedCount}
      localIndex={localIndex}
      options={config.options({ aiBotOptions, emailOptions, messengersOptions })}
      prices={config.prices({ aiBotPricing, emailPricing, messengersPricing })}
      unitLabel={config.unitLabel}
      itemLabel={config.itemLabel}
      hint={config.hint()}
      handleToggle={handleToggle}
      handleSelect={handleSelect}
      withModuleWrapper={config.withModuleWrapper}
      hideMainSwitch={config.hideMainSwitch}
    />
  )
}

export default AddonPriceControl

type DropdownArrays = {
  aiBotOptions: number[]
  aiBotPricing: number[]
  emailOptions: number[]
  emailPricing: number[]
  messengersOptions: number[]
  messengersPricing: number[]
}

const addonDropdownConfig = {
  ai: {
    withModuleWrapper: true,
    hideMainSwitch: true,
    unitLabel: "вопросов",
    itemLabel: "вопросов",
    options: ({ aiBotOptions }: DropdownArrays) => aiBotOptions,
    prices: ({ aiBotPricing }: DropdownArrays) => aiBotPricing,
    hint: () => ({
      title: "Как считаются вопросы",
      content: (
        <>
          Учитываются вопросы, в&nbsp;которых AI-бот принял участие.
          <br />
          <br />
          Новый вопрос создаётся, когда пользователь спустя время пишет сообщение в&nbsp;закрытый диалог.
        </>
      ),
    }),
  },
  messengers: {
    withModuleWrapper: true,
    hideMainSwitch: true,
    unitLabel: "сообщений",
    itemLabel: "сообщений",
    options: ({ messengersOptions }: DropdownArrays) => messengersOptions,
    prices: ({ messengersPricing }: DropdownArrays) => messengersPricing,
    hint: () => ({
      title: "Как считаются сообщения",
      content: <>Считаются все отправки по всем подключённым мессенджерам за месяц: из триггерных сценариев, массовых рассылок и отдельных сообщений подписчикам бота.</>,
    }),
  },
  email: {
    withModuleWrapper: true,
    hideMainSwitch: true,
    unitLabel: "писем",
    itemLabel: "писем",
    options: ({ emailOptions }: DropdownArrays) => emailOptions,
    prices: ({ emailPricing }: DropdownArrays) => emailPricing,
    hint: () => ({
      title: "Как считаются письма",
      content: (
        <>
          Считается количество отправленных ручных и автоматических email-рассылок, оповещений на почту операторам, дублирование сообщений из чата на почту
          пользователей, письма о подтверждении подписки на почтовые рассылки.
        </>
      ),
    }),
  },
} as const
